import React, { useEffect, useRef, useState } from "react";

/**
 * Reveals children with a fade + slide-up animation when scrolled into view.
 * Uses IntersectionObserver — fires once, then disconnects.
 *
 * Props:
 *  - delay (ms): stagger delay before this element animates in
 *  - as (component or tag): wrapper element (default "div")
 *  - amount (0..1): how much of the element must be visible before triggering
 */
const Reveal = ({
  children,
  delay = 0,
  as: Tag = "div",
  amount = 0.12,
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: amount, rootMargin: "0px 0px -80px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [amount]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "is-revealed" : ""} ${className}`}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
